import { useState } from "react";
import { Link, NavLink, Navigate, Route, Routes, useLocation } from "react-router-dom";
import { getStoredEnteredName, setStoredEnteredName } from "./lib/session";
import { ApparatusScreen } from "./screens/ApparatusScreen";
import { BagDetailScreen } from "./screens/BagDetailScreen";
import { ChangeLogScreen } from "./screens/ChangeLogScreen";
import { ComplianceScreen } from "./screens/ComplianceScreen";
import { OrderingScreen } from "./screens/PlanningScreen";
import { StockRoomScreen } from "./screens/StockRoomScreen";
import { TruckCheckScreen } from "./screens/TruckCheckScreen";
import { VersionHistoryScreen } from "./screens/VersionHistoryScreen";
import { ViewRefreshProvider, useViewRefreshState } from "./lib/viewRefresh";
import { ConnectionStatus } from "./components/ConnectionStatus";
import dShiftFooterLogo from "./assets/d-shift-2026-footer.png";
import mtlfdLogo from "./assets/mtlfd-logo.svg";

const APP_VERSION = "0.8.9";

export default function App() {
  return (
    <ViewRefreshProvider>
      <AppShell />
    </ViewRefreshProvider>
  );
}

function AppShell() {
  const location = useLocation();
  const [enteredName, setEnteredName] = useState(() => getStoredEnteredName());
  const viewRefresh = useViewRefreshState();
  const isWideRoute = location.pathname === "/stock" || location.pathname === "/log";
  const isPreviewEnvironment =
    typeof window !== "undefined" &&
    (window.location.hostname.startsWith("preview.") ||
      window.location.hostname.includes("preview-") ||
      window.location.hostname === "localhost" ||
      window.location.hostname === "127.0.0.1");

  const rememberName = (name: string) => {
    const trimmed = name.trim();
    if (!trimmed) return;
    setStoredEnteredName(trimmed);
    setEnteredName(trimmed);
  };

  const renderRefreshButton = (placement: "floating" | "navigation") =>
    viewRefresh.available ? (
      <button
        className={`view-refresh-button view-refresh-button-${placement}`}
        type="button"
        onClick={() => void viewRefresh.refresh()}
        disabled={viewRefresh.disabled || viewRefresh.refreshing}
        aria-label={viewRefresh.refreshing ? "Refreshing current view" : "Refresh current view"}
        aria-busy={viewRefresh.refreshing}
        title={viewRefresh.disabled ? viewRefresh.disabledReason || "Refresh is unavailable right now." : "Refresh current view"}
      >
        <svg
          className={`view-refresh-icon${viewRefresh.refreshing ? " is-spinning" : ""}`}
          viewBox="0 0 24 24"
          aria-hidden="true"
        >
          <path d="M20 6v5h-5M4 18v-5h5M6.1 9a7 7 0 0 1 11.2-2.2L20 9M4 15l2.7 2.2A7 7 0 0 0 17.9 15" />
        </svg>
        <span className="view-refresh-label">{viewRefresh.refreshing ? "Refreshing" : "Refresh"}</span>
      </button>
    ) : null;

  return (
    <div className={`app-shell${isPreviewEnvironment ? " preview-mode" : ""}`}>
      {isPreviewEnvironment ? (
        <p className="preview-banner" role="status">
          PREVIEW/TESTING - NOT FOR PRODUCTION CHANGES
        </p>
      ) : null}
      <header className="topbar">
        <div className="topbar-inner">
          <div className="brand">
            <div className="brand-mark" aria-hidden>
              <img
                className="brand-logo-sprite"
                src={mtlfdLogo}
                alt=""
                loading="eager"
                decoding="async"
              />
            </div>
            <div className="brand-copy">
              <p className="brand-eyebrow">Mt. Lebanon Fire Dept</p>
              <h1 className="brand-title">QRS Inventory</h1>
            </div>
          </div>
          <ConnectionStatus />
        </div>
      </header>

      {renderRefreshButton("floating")}

      <main className={`page${isWideRoute ? " page-wide" : ""}`}>
        <Routes>
          <Route path="/" element={<ApparatusScreen />} />
          <Route path="/status" element={<Navigate to="/compliance" replace />} />
          <Route path="/dashboard" element={<Navigate to="/compliance" replace />} />
          <Route path="/apparatus" element={<ApparatusScreen />} />
          <Route path="/apparatus/:apparatusId" element={<ApparatusScreen />} />
          <Route path="/bags/:bagId" element={<BagDetailScreen enteredName={enteredName} rememberName={rememberName} />} />
          <Route path="/truck-check" element={<TruckCheckScreen enteredName={enteredName} rememberName={rememberName} />} />
          <Route path="/stock" element={<StockRoomScreen enteredName={enteredName} rememberName={rememberName} />} />
          <Route path="/compliance" element={<ComplianceScreen />} />
          <Route path="/ordering" element={<OrderingScreen />} />
          <Route path="/log" element={<ChangeLogScreen enteredName={enteredName} rememberName={rememberName} />} />
          <Route path="/planning" element={<Navigate to="/ordering" replace />} />
          <Route path="/versions" element={<VersionHistoryScreen />} />
        </Routes>
      </main>

      <footer className={`shift-footer${isWideRoute ? " shift-footer-wide" : ""}`} aria-label="D-Shift footer note">
        <span className="shift-footer-note">
          <img className="shift-footer-logo" src={dShiftFooterLogo} alt="" loading="lazy" decoding="async" />
          <span>D-Shift Gets It Done</span>
          <Link className="shift-footer-version" to="/versions" aria-label="View version history">
            v{APP_VERSION}
          </Link>
        </span>
      </footer>

      <nav className="bottom-nav">
        <NavLink to="/" className="nav-item">
          Apparatus
        </NavLink>
        <NavLink to="/truck-check" className="nav-item">
          Truck Check
        </NavLink>
        <NavLink to="/stock" className="nav-item">
          Stock
        </NavLink>
        <NavLink to="/compliance" className="nav-item">
          Compliance
        </NavLink>
        <NavLink to="/ordering" className="nav-item">
          Ordering
        </NavLink>
        <NavLink to="/log" className="nav-item">
          Log
        </NavLink>
        {renderRefreshButton("navigation")}
      </nav>

      {viewRefresh.status ? (
        <div
          className={`view-refresh-toast${
            viewRefresh.statusType ? ` view-refresh-toast-${viewRefresh.statusType}` : ""
          }`}
          role="status"
          aria-live="polite"
        >
          {viewRefresh.status}
        </div>
      ) : null}
    </div>
  );
}
